import { getPermission } from '@auth';
import { ROUTER_NAME, UIPaths } from '@consts';
import { useAccountsService } from '@hooks';
import {
  CreateIcon,
  HomeIcon,
  MoreVerticalIcon,
  MountainWithFlagIcon,
  SearchMenuIcon
} from '@icons';
import MessagesIcon from '@icons/MessagesIcon';
import { PermissionEntityType, PermissionType } from '@models';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import BottomNavBarMoreBottomSheet from './BottomNavBarMoreBottomSheet';
import CreateEntitiesMenu from './CreateEntitiesMenu';

interface BottomNavBarProps {
  goalsRecommendationsCount?: number;
}

export default function BottomNavBar({ goalsRecommendationsCount = 0 }: BottomNavBarProps) {
  const router = useRouter();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState({
    show: false,
    position: { x: 0, y: 0 }
  });
  const { accountMe } = useAccountsService();

  let taskCreatePermission = useMemo(() => {
    if (!accountMe) return null;
    return getPermission(accountMe.__permissions, PermissionType.CREATE, PermissionEntityType.TASK);
  }, [accountMe]);

  let goalCreatePermission = useMemo(() => {
    if (!accountMe) return null;
    return getPermission(accountMe.__permissions, PermissionType.CREATE, PermissionEntityType.GOAL);
  }, [accountMe]);

  const messagesUnreadCount = 0;

  const onJumpToLink = (link: string) => {
    return () => {
      router.push(link);
    };
  };

  const onExploreClick = () => {
    onJumpToLink(UIPaths.explore())();
  };

  const onOpenCreateMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsCreateMenuOpen({
      show: true,
      position: { x: e.clientX, y: e.clientY }
    });
  };

  const menuItems = [
    {
      icon: <HomeIcon size={36} />,
      onClick: onJumpToLink(UIPaths.home()),
      active: router.pathname === ROUTER_NAME.HOME,
      badge: null,
      dataTestId: 'bottom-nav-item-home'
    },
    {
      icon: <MountainWithFlagIcon size={36} />,
      onClick: onJumpToLink(UIPaths.goalSubscriptions()),
      active: router.pathname === ROUTER_NAME.GOAL_SUBSCRIPTIONS,
      badge: null,
      dataTestId: 'bottom-nav-item-subscriptions'
    },
    {
      icon: <MessagesIcon />,
      onClick: onJumpToLink(UIPaths.messages()),
      active: router.pathname === ROUTER_NAME.MESSAGES,
      badge: messagesUnreadCount > 0 ? messagesUnreadCount : null,
      dataTestId: 'bottom-nav-item-messages'
    },
    {
      icon: <SearchMenuIcon size={36} />,
      onClick: onExploreClick,
      active: router.pathname === ROUTER_NAME.EXPLORE,
      badge: goalsRecommendationsCount > 0 ? goalsRecommendationsCount : null,
      dataTestId: 'bottom-nav-item-explore'
    },
    {
      icon: <CreateIcon size={36} />,
      onClick: onOpenCreateMenu,
      active: isCreateMenuOpen.show,
      badge: null,
      dataTestId: 'bottom-nav-item-create'
    }
  ];

  const isInMore = useMemo(() => {
    return router.pathname === ROUTER_NAME.TASKS || router.pathname === ROUTER_NAME.GOALS;
  }, [router.pathname]);

  return (
    <>
      <div
        className={classNames(
          'w-full flex flex-row',
          'border-t-[1px]',
          setupForDark('border-primary bg-nav')
        )}
      >
        <div className='flex flex-row w-full justify-between px-0'>
          {menuItems.map((item, i) => (
            <div
              key={i}
              data-testid={item.dataTestId}
              onClick={(e) => !item.active && item.onClick(e)}
              className={classNames(
                {
                  'flex flex-1 items-center justify-center': true,
                  'py-md': true,
                  'cursor-pointer': true
                },
                item.active
                  ? setupForDark('text-black bg-violet-700')
                  : setupForDark('text-icon-secondary')
              )}
            >
              <div className='relative'>
                {item.icon}
                {item.badge !== null && item.badge !== undefined && (
                  <div
                    className={classNames(
                      'absolute flex items-center justify-center',
                      'top-[1px] left-[15px]',
                      'min-w-[13px] h-[13px]',
                      'px-[2px]',
                      'rounded-full',
                      setupForDark('bg-red-700')
                    )}
                  >
                    <div
                      className={classNames(
                        'text-[8px] font-medium leading-[1.3] text-center tracking-[0.16px]',
                        setupForDark('text-white')
                      )}
                    >
                      {item.badge}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {taskCreatePermission && goalCreatePermission && (
            <div
              className={classNames(
                {
                  'flex flex-1 items-center justify-center': true,
                  'py-md': true,
                  'cursor-pointer': true
                },
                isInMore || isMoreMenuOpen
                  ? setupForDark('text-black bg-violet-700')
                  : setupForDark('text-icon-secondary')
              )}
              onClick={() => setIsMoreMenuOpen(true)}
            >
              <MoreVerticalIcon size={36} />
            </div>
          )}
          {isMoreMenuOpen && (
            <BottomNavBarMoreBottomSheet
              isOpen={isMoreMenuOpen}
              onClose={() => setIsMoreMenuOpen(false)}
            />
          )}
          {isCreateMenuOpen.show && (
            <CreateEntitiesMenu
              isOpen={isCreateMenuOpen.show}
              onClose={() =>
                setIsCreateMenuOpen({
                  show: false,
                  position: { x: 0, y: 0 }
                })
              }
              position={isCreateMenuOpen.position}
            />
          )}
        </div>
      </div>
    </>
  );
}
